<script setup>
// Modelos de e-mail transacional (T-90). O backend existe desde 07/08 com CRUD completo, mas a
// tela nunca foi construída: na prática o texto institucional do e-mail de encerramento só era
// editável por comando no servidor, enquanto a devolutiva já afirmava que o clube edita pelo
// painel. Este arquivo fecha essa distância.
//
// Espelha Configurações → Parceiros, que é o padrão de CRUD admin do fork.
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

import EmailTemplatesAPI from 'dashboard/api/fandata/emailTemplates';

const { t } = useI18n();

// As variáveis que o disparador realmente injeta (Fandata::EmailDispatcher + os drops do
// Chatwoot). Lista fechada de propósito: oferecer uma variável que não existe faria o texto
// sair com o marcador cru no e-mail do torcedor.
const VARIAVEIS = [
  '{{contact.name}}',
  '{{contact.first_name}}',
  '{{contact.email}}',
  '{{account.name}}',
  '{{conversation.protocolo}}',
];
// Só o modelo de encerramento recebe esta: é o texto que o agente escreve ao fechar.
const SLUG_ENCERRAMENTO = 'encerramento';

const isFetching = ref(true);
const templates = ref([]);

const showForm = ref(false);
const editing = ref(null);
const savingForm = ref(false);
const form = reactive({ nome: '', assunto: '', corpo: '', ativo: true });

const showDelete = ref(false);
const selected = ref(null);
const deleting = ref(false);

const tableHeaders = computed(() => [
  t('EMAIL_TEMPLATES_ADMIN.TABLE.NOME'),
  t('EMAIL_TEMPLATES_ADMIN.TABLE.ASSUNTO'),
  t('EMAIL_TEMPLATES_ADMIN.TABLE.STATUS'),
  t('EMAIL_TEMPLATES_ADMIN.TABLE.ACTIONS'),
]);

const formTitle = computed(() =>
  editing.value
    ? t('EMAIL_TEMPLATES_ADMIN.FORM.EDIT_TITLE')
    : t('EMAIL_TEMPLATES_ADMIN.FORM.NEW_TITLE')
);

const isFormValid = computed(
  () => form.nome.trim() && form.assunto.trim() && form.corpo.trim()
);

// O exemplo do corpo é montado aqui, e não na tradução: o vue-i18n lê `{{...}}` como chave
// aninhada e RECUSA a mensagem, derrubando o render do modal inteiro. Foi o que aconteceu.
// De quebra, o nome da variável passa a ter uma fonte só.
const corpoPlaceholder = computed(() =>
  t('EMAIL_TEMPLATES_ADMIN.FORM.CORPO_PLACEHOLDER', { exemplo: VARIAVEIS[0] })
);

const variaveisDoForm = computed(() =>
  editing.value?.slug === SLUG_ENCERRAMENTO
    ? [...VARIAVEIS, '{{mensagem_agente}}']
    : VARIAVEIS
);

const loadAll = async () => {
  isFetching.value = true;
  try {
    const { data } = await EmailTemplatesAPI.listManage();
    templates.value = data.templates || [];
  } catch (error) {
    useAlert(t('EMAIL_TEMPLATES_ADMIN.FORM.ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const resetForm = () => {
  form.nome = '';
  form.assunto = '';
  form.corpo = '';
  form.ativo = true;
  editing.value = null;
};

const openNew = () => {
  resetForm();
  showForm.value = true;
};

const openEdit = template => {
  editing.value = template;
  form.nome = template.nome;
  form.assunto = template.assunto;
  form.corpo = template.corpo;
  form.ativo = template.ativo;
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  resetForm();
};

// Inserir no fim do corpo em vez de na posição do cursor: é previsível, e o clube vai colar a
// variável e mover, não digitá-la de memória. Errar o nome dela faz o marcador ir cru no e-mail.
const inserirVariavel = variavel => {
  form.corpo = `${form.corpo}${variavel}`;
};

const saveTemplate = async () => {
  if (!isFormValid.value) return;
  savingForm.value = true;
  // O `slug` não vai no payload nem quando existe: ele é gerado no create e é o que os
  // gatilhos de automação referenciam. Editá-lo desligaria o disparo em silêncio.
  const payload = {
    nome: form.nome.trim(),
    assunto: form.assunto.trim(),
    corpo: form.corpo,
    ativo: form.ativo,
  };
  try {
    if (editing.value) {
      const { data } = await EmailTemplatesAPI.update(editing.value.id, payload);
      templates.value = templates.value.map(x => (x.id === data.id ? data : x));
    } else {
      const { data } = await EmailTemplatesAPI.create(payload);
      templates.value = [...templates.value, data];
    }
    useAlert(t('EMAIL_TEMPLATES_ADMIN.FORM.SAVED'));
    closeForm();
  } catch (error) {
    const msg =
      error?.response?.data?.errors?.join(', ') ||
      t('EMAIL_TEMPLATES_ADMIN.FORM.ERROR');
    useAlert(msg);
  } finally {
    savingForm.value = false;
  }
};

// LIGAR E DESLIGAR DIRETO NA LISTA, SEM ABRIR O MODELO.
//
// Relato do clube em 13/10: um e-mail de encerramento saiu com o modelo desligado na tela. A
// causa não foi o disparo, foi o DESLIGAMENTO que nunca chegou ao banco: o único caminho era
// salvar o modelo inteiro, e o corpo dele é HTML, que o firewall da borda recusa antes do Rails.
//
// Aqui vai só o booleano. É também o caminho certo pela urgência: desligar um disparo acontece
// quando algo já está saindo errado para o torcedor, e não é hora de abrir um editor.
const alternandoAtivo = ref(null);

const alternarAtivo = async template => {
  alternandoAtivo.value = template.id;
  try {
    const { data } = await EmailTemplatesAPI.definirAtivo(
      template.id,
      !template.ativo
    );
    templates.value = templates.value.map(x => (x.id === data.id ? data : x));
    useAlert(
      t(
        data.ativo
          ? 'EMAIL_TEMPLATES_ADMIN.LIGADO'
          : 'EMAIL_TEMPLATES_ADMIN.DESLIGADO'
      )
    );
  } catch (error) {
    useAlert(t('EMAIL_TEMPLATES_ADMIN.FORM.ERROR'));
  } finally {
    alternandoAtivo.value = null;
  }
};

const openDelete = template => {
  selected.value = template;
  showDelete.value = true;
};

const closeDelete = () => {
  showDelete.value = false;
  selected.value = null;
};

const confirmDelete = async () => {
  if (!selected.value) return;
  deleting.value = true;
  try {
    await EmailTemplatesAPI.delete(selected.value.id);
    templates.value = templates.value.filter(x => x.id !== selected.value.id);
    useAlert(t('EMAIL_TEMPLATES_ADMIN.DELETE.SUCCESS'));
    closeDelete();
  } catch (error) {
    const msg =
      error?.response?.data?.error || t('EMAIL_TEMPLATES_ADMIN.DELETE.ERROR');
    useAlert(msg);
    closeDelete();
  } finally {
    deleting.value = false;
  }
};

onMounted(loadAll);
</script>

<template>
  <SettingsLayout
    :is-loading="isFetching"
    :loading-message="$t('EMAIL_TEMPLATES_ADMIN.HEADER')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('EMAIL_TEMPLATES_ADMIN.HEADER')"
        :description="$t('EMAIL_TEMPLATES_ADMIN.DESCRIPTION')"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-4 mt-4">
        <div class="flex items-start justify-between gap-4">
          <p class="max-w-2xl text-sm text-n-slate-11">
            {{ $t('EMAIL_TEMPLATES_ADMIN.HINT') }}
          </p>
          <Button
            :label="$t('EMAIL_TEMPLATES_ADMIN.ADD')"
            icon="i-lucide-plus"
            size="sm"
            class="flex-shrink-0"
            @click="openNew"
          />
        </div>

        <BaseTable
          :headers="tableHeaders"
          :items="templates"
          :no-data-message="$t('EMAIL_TEMPLATES_ADMIN.EMPTY')"
        >
          <template #row="{ items }">
            <BaseTableRow v-for="tpl in items" :key="tpl.id" :item="tpl">
              <template #default>
                <BaseTableCell>
                  <div class="flex flex-col">
                    <span class="text-body-main text-n-slate-12">
                      {{ tpl.nome }}
                    </span>
                    <!-- O slug aparece porque é ele que as automações referenciam: sem isso,
                         quem monta a regra não tem como saber qual modelo escolher. -->
                    <span class="font-mono text-xs text-n-slate-10">
                      {{ tpl.slug }}
                    </span>
                  </div>
                </BaseTableCell>
                <BaseTableCell>
                  <span class="text-body-main text-n-slate-11 line-clamp-2">
                    {{ tpl.assunto }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <button
                    v-tooltip.top="$t('EMAIL_TEMPLATES_ADMIN.ALTERNAR')"
                    class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium disabled:opacity-50"
                    :class="
                      tpl.ativo
                        ? 'bg-n-teal-3 text-n-teal-11 hover:bg-n-teal-4'
                        : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
                    "
                    :disabled="alternandoAtivo === tpl.id"
                    @click="alternarAtivo(tpl)"
                  >
                    {{
                      tpl.ativo
                        ? $t('EMAIL_TEMPLATES_ADMIN.ATIVO')
                        : $t('EMAIL_TEMPLATES_ADMIN.INATIVO')
                    }}
                  </button>
                </BaseTableCell>
                <BaseTableCell align="end">
                  <div class="flex flex-shrink-0 gap-3 justify-end">
                    <Button
                      icon="i-woot-edit-pen"
                      slate
                      sm
                      @click="openEdit(tpl)"
                    />
                    <Button
                      icon="i-woot-bin"
                      slate
                      sm
                      class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                      @click="openDelete(tpl)"
                    />
                  </div>
                </BaseTableCell>
              </template>
            </BaseTableRow>
          </template>
        </BaseTable>
      </div>
    </template>

    <woot-modal v-model:show="showForm" :on-close="closeForm" size="medium">
      <div class="flex flex-col gap-4 p-8">
        <h2 class="text-lg font-medium text-n-slate-12">{{ formTitle }}</h2>

        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('EMAIL_TEMPLATES_ADMIN.FORM.NOME_LABEL') }}
          </span>
          <input
            v-model="form.nome"
            type="text"
            :placeholder="$t('EMAIL_TEMPLATES_ADMIN.FORM.NOME_PLACEHOLDER')"
            class="px-3 py-2 w-full text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-n-brand"
          />
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('EMAIL_TEMPLATES_ADMIN.FORM.ASSUNTO_LABEL') }}
          </span>
          <input
            v-model="form.assunto"
            type="text"
            :placeholder="$t('EMAIL_TEMPLATES_ADMIN.FORM.ASSUNTO_PLACEHOLDER')"
            class="px-3 py-2 w-full text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-n-brand"
          />
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('EMAIL_TEMPLATES_ADMIN.FORM.CORPO_LABEL') }}
          </span>
          <textarea
            v-model="form.corpo"
            rows="12"
            :placeholder="corpoPlaceholder"
            class="px-3 py-2 w-full font-mono text-xs rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-n-brand"
          />
        </label>

        <!-- Sem esta lista a tela seria só um CRUD: quem escreve o texto não teria como saber
             o que dá para personalizar, e um nome de variável errado sai cru no e-mail. -->
        <div class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('EMAIL_TEMPLATES_ADMIN.FORM.VARIAVEIS_LABEL') }}
          </span>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="v in variaveisDoForm"
              :key="v"
              type="button"
              class="px-1.5 py-0.5 font-mono text-xs rounded border border-n-weak text-n-slate-11 hover:bg-n-alpha-2"
              :title="$t('EMAIL_TEMPLATES_ADMIN.FORM.VARIAVEIS_HINT')"
              @click="inserirVariavel(v)"
            >
              {{ v }}
            </button>
          </div>
        </div>

        <div class="flex gap-4 items-center">
          <label class="flex gap-2 items-center">
            <input v-model="form.ativo" type="checkbox" />
            <span class="text-sm text-n-slate-12">
              {{ $t('EMAIL_TEMPLATES_ADMIN.FORM.ATIVO_LABEL') }}
            </span>
          </label>
        </div>

        <p v-if="editing" class="-mt-1 text-xs text-n-slate-10">
          {{ $t('EMAIL_TEMPLATES_ADMIN.FORM.SLUG_HINT', { slug: editing.slug }) }}
        </p>

        <div class="flex gap-3 justify-end mt-2">
          <Button
            :label="$t('EMAIL_TEMPLATES_ADMIN.FORM.CANCEL')"
            variant="faded"
            color="slate"
            size="sm"
            @click="closeForm"
          />
          <Button
            :label="$t('EMAIL_TEMPLATES_ADMIN.FORM.SAVE')"
            size="sm"
            :disabled="!isFormValid"
            :is-loading="savingForm"
            @click="saveTemplate"
          />
        </div>
      </div>
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDelete"
      :on-close="closeDelete"
      :on-confirm="confirmDelete"
      :title="$t('EMAIL_TEMPLATES_ADMIN.DELETE.CONFIRM')"
      :message="selected ? selected.nome : ''"
      :confirm-text="$t('EMAIL_TEMPLATES_ADMIN.DELETE.YES')"
      :reject-text="$t('EMAIL_TEMPLATES_ADMIN.DELETE.NO')"
    />
  </SettingsLayout>
</template>
